import { tokensRaw } from '@vp/swan'
import { MINIMUM_TILE_DEFAULT_WIDTH, MINIMUM_TILE_LARGE_WIDTH, TILE_PREVIEW_SIZE } from '~/app/constants'
import { PREVIEW_TYPE, SCENE_TYPE } from 'shared/constants'
import { addQueryParamToUrl } from '~/shared/url'
import type { GalleryContentQuery } from 'shared/types/gallery/content-query'
import type { GalleryDesignsRenderablePreviewInfo } from 'shared/types/gallery/designs'
import type { GalleryModelsUrl } from 'shared/types/gallery/models'

type RenderingURLQueryParams = {
  url: string
  templateToken: string
  previewWidth: number
  sceneUri?: string
  imageCacheVersion: string | null
  sceneType?: SCENE_TYPE
}

export const DEFAULT_BACKGROUND_COLOR = tokensRaw.SwanSemColorBgStrong.substring(1)

const DEFAULT_DYNAMIC_PARAMS: Record<string, string> = {
  format: 'auto',
  quality: '95',
  showerr: 'true',
  category: 'gal6',
}

const addRenderingURLQueryParams = ({ url, templateToken, previewWidth, sceneUri, imageCacheVersion = null, sceneType }: RenderingURLQueryParams): string => {
  const updatedURL = new URL(url.startsWith('//') ? `https:${url}` : url)
  const isVortexScene = sceneType === SCENE_TYPE.VORTEX_RASTER
  const isDynamicScene = sceneType === SCENE_TYPE.DYNAMIC

  const instructionsUriParam = isVortexScene ? 'renderingInstructionsUrl' : 'instructions_uri'

  const instructionsUrlString = updatedURL.searchParams.get(instructionsUriParam)

  if (!instructionsUrlString) {
    throw new Error('No instructions URI')
  }
  const instructionsUrl = new URL(instructionsUrlString)

  if (isDynamicScene) {
    // Ignore projections for dynamic scenes
    instructionsUrl.searchParams.set('ignoreProjection', 'true')
  }

  const newSceneUri = sceneUri && imageCacheVersion ? addQueryParamToUrl(sceneUri, 'icv', imageCacheVersion) : sceneUri

  const newParams: Record<string, string | undefined> = isVortexScene
    ? {
        bgColor: DEFAULT_BACKGROUND_COLOR,
      }
    : {
        ...DEFAULT_DYNAMIC_PARAMS,
        merchant_metadata: templateToken,
        scene: newSceneUri,
        bgcolor: DEFAULT_BACKGROUND_COLOR,
      }

  newParams[instructionsUriParam] = instructionsUrl.toString()
  newParams.width = Number(previewWidth).toString()

  for (const [key, value] of Object.entries(newParams)) {
    if (value !== undefined) {
      updatedURL.searchParams.set(key, value)
    }
  }

  return updatedURL.toString()
}

export const getPreviewData = (
  previewInfo: GalleryContentQuery.PreviewInfo,
  previewType: PREVIEW_TYPE,
  previewUrls: GalleryContentQuery.PreviewUrls,
  previewSize: GalleryModelsUrl.ValidParsedQsValue<string> = TILE_PREVIEW_SIZE.DEFAULT,
  imageCacheVersion: GalleryModelsUrl.ValidParsedQsValue<string> = null,
): GalleryDesignsRenderablePreviewInfo => {
  let previewWidth = (previewSize && previewSize === TILE_PREVIEW_SIZE.LARGE ? MINIMUM_TILE_LARGE_WIDTH : MINIMUM_TILE_DEFAULT_WIDTH) * 2

  if (previewType === PREVIEW_TYPE.QUICKVIEW) {
    previewWidth *= 3
  }

  const sceneUri = (previewInfo.scenes && previewInfo.scenes[PREVIEW_TYPE.TILE]?.uri) || undefined
  const sceneType = previewInfo.scenes && previewInfo.scenes[PREVIEW_TYPE.TILE]?.sceneType
  const commonPreviewURLParams = {
    templateToken: previewInfo.templateToken,
    previewWidth,
    imageCacheVersion,
    sceneUri,
    sceneType,
  }

  const builtPreviewUrls = {
    size1x: addRenderingURLQueryParams({
      ...commonPreviewURLParams,
      url: previewUrls?.size1x,
    }),
    size2x: addRenderingURLQueryParams({
      ...commonPreviewURLParams,
      url: previewUrls?.size2x,
    }),
  }

  return {
    previewInfo,
    previewWidth,
    previewsUrls: builtPreviewUrls,
  }
}
